/**
 * Centralized localStorage utilities for clearing authentication and application data
 *
 * This module provides DRY functions for localStorage operations, eliminating duplication
 * across TokenManager, axiosInstance, and AuthContext components.
 */

import {
	ACCESS_TOKEN_EXP_KEY,
	ACCESS_TOKEN_KEY,
	REFRESH_TOKEN_KEY,
	SELECTED_HOUSE_ID_KEY,
} from '@/utils/storage/constants.ts';

/**
 * Clears all authentication-related localStorage entries
 * Used during logout or when authentication becomes invalid
 */
export function clearAuthStorage(): void {
	try {
		localStorage.removeItem(ACCESS_TOKEN_KEY);
		localStorage.removeItem(REFRESH_TOKEN_KEY);
		localStorage.removeItem(ACCESS_TOKEN_EXP_KEY);
		localStorage.removeItem('role');
		localStorage.removeItem('user');
	} catch {
		// Ignore storage errors (e.g., in SSR/test environments)
	}
}

/**
 * Clears task-related localStorage entries
 * Used to prevent stale task data after logout or context switches
 */
export function clearTaskStorage(): void {
	const keysToRemove: string[] = [];
	try {
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && (key.includes('maintenance-tasks') || key === 'tasks')) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach((k) => localStorage.removeItem(k));
	} catch {
		// Ignore storage errors
	}
}

/**
 * Clears selected house localStorage entries
 * Used to reset house context during logout or house switching
 */
export function clearHouseStorage(): void {
	try {
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && (key.includes('selectedHouse') || key === SELECTED_HOUSE_ID_KEY)) {
				localStorage.removeItem(key);
			}
		}
	} catch {
		// Ignore storage errors
	}
}

/**
 * Clears guest-specific metadata localStorage entries
 * Used when guest session expires or is manually terminated
 */
export function clearGuestStorage(): void {
	try {
		safeRemoveItem('guest_scopes');
	} catch {
		// Ignore storage errors
	}
}

/**
 * Comprehensive cleanup of all application localStorage data
 * Used during complete logout to ensure no stale data remains
 */
export function clearAllAppStorage(): void {
	clearAuthStorage();
	clearTaskStorage();
	clearHouseStorage();
	clearGuestStorage();
}

/**
 * Safe localStorage item removal with error handling
 */
export function safeRemoveItem(key: string): void {
	try {
		localStorage.removeItem(key);
	} catch {
		// Ignore storage errors
	}
}

/**
 * Safe localStorage item setting with error handling
 */
export function safeSetItem(key: string, value: string): void {
	try {
		localStorage.setItem(key, value);
	} catch {
		// Ignore storage errors (e.g., in SSR/test environments, quota exceeded)
	}
}

/**
 * Safe localStorage item getting with error handling
 */
export function safeGetItem(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}
